<script setup lang="ts">
/**
 * Pages sidebar for the open narrative — the left panel of the narrative detail
 * page (see docs_for_claude/figma-designs/latest/narrative-blank.png). Renders
 * the narrative's pages as a tree (nested up to MAX_DEPTH child levels below the
 * roots), plus a "New page" button. Mirrors DialogSessionList's rename/delete
 * affordances: double-click a page title to rename it inline (Enter / blur
 * saves, Esc cancels), and a per-page kebab menu offers Rename, Add sub-page,
 * and Delete, the last behind a confirm dialog. Selecting a page emits `select`.
 *
 * All persistence goes through narrativesStore; this component is presentational
 * plus local edit/menu state. The parent owns which page is active.
 */
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNarrativesStore, type NarrativePage } from '../../stores/narrativesStore';

const props = defineProps<{
    narrativeId: string;
    activePageId: string | null;
}>();

const emit = defineEmits<{
    (e: 'select', pageId: string): void;
    (e: 'page-deleted', pageId: string): void;
}>();

const { t } = useI18n();
const store = useNarrativesStore();

// Nesting is limited to three child levels beneath the roots (depth 0). A page
// at MAX_DEPTH can't take children, so its "Add sub-page" action is hidden.
const MAX_DEPTH = 3;

interface FlatPage {
    page: NarrativePage;
    depth: number;
}

// Depth-first flatten of the page forest so the template can render a single
// list with indentation per depth. Roots are pages with no parent (or whose
// parent is missing); children keep insertion order.
const flatPages = computed<FlatPage[]>(() => {
    const pages = store.current?.pages ?? [];
    const ids = new Set(pages.map((p) => p.id));
    const byParent = new Map<string | null, NarrativePage[]>();
    for (const page of pages) {
        const key = page.parent_id && ids.has(page.parent_id) ? page.parent_id : null;
        const list = byParent.get(key) ?? [];
        list.push(page);
        byParent.set(key, list);
    }
    const out: FlatPage[] = [];
    const walk = (parentId: string | null, depth: number) => {
        for (const page of byParent.get(parentId) ?? []) {
            out.push({ page, depth });
            walk(page.id, depth + 1);
        }
    };
    walk(null, 0);
    return out;
});

const depthOf = (pageId: string) => flatPages.value.find((f) => f.page.id === pageId)?.depth ?? 0;

const pageById = (pageId: string | null): NarrativePage | undefined =>
    pageId ? store.current?.pages.find((p) => p.id === pageId) : undefined;

// ── Inline rename ─────────────────────────────────────────────────────────
const editingPageId = ref<string | null>(null);
const editingTitle = ref('');
const renameInput = ref<HTMLInputElement | null>(null);
// Guards blur-vs-Enter double-submit: once one fires, the other no-ops.
let renameCommitted = false;

const startRename = async (page: NarrativePage) => {
    closeMenu();
    editingPageId.value = page.id;
    editingTitle.value = page.title;
    renameCommitted = false;
    await nextTick();
    renameInput.value?.focus();
    renameInput.value?.select();
};

const commitRename = async () => {
    if (renameCommitted) return;
    renameCommitted = true;
    const pageId = editingPageId.value;
    const title = editingTitle.value.trim();
    editingPageId.value = null;
    if (!pageId) return;
    const original = pageById(pageId);
    // Nothing to do on an empty or unchanged title.
    if (!title || title === original?.title) return;
    try {
        await store.renamePage(props.narrativeId, pageId, title);
    } catch {
        // Store surfaces the error; leave the sidebar as-is.
    }
};

const cancelRename = () => {
    renameCommitted = true;
    editingPageId.value = null;
};

// ── Kebab menu ────────────────────────────────────────────────────────────
const menuPageId = ref<string | null>(null);
const menuPosition = ref({ x: 0, y: 0 });

const openMenu = (event: MouseEvent, pageId: string) => {
    event.stopPropagation();
    menuPageId.value = pageId;
    menuPosition.value = { x: event.clientX, y: event.clientY };
};

const closeMenu = () => {
    menuPageId.value = null;
};

// ── Delete ────────────────────────────────────────────────────────────────
const showDeleteConfirm = ref(false);
const pageToDelete = ref<NarrativePage | null>(null);
const deleting = ref(false);

// A narrative must keep at least one page, so the last page can't be deleted.
const canDelete = computed(() => (store.current?.pages.length ?? 0) > 1);

const confirmDelete = (page: NarrativePage) => {
    closeMenu();
    pageToDelete.value = page;
    showDeleteConfirm.value = true;
};

const cancelDelete = () => {
    if (deleting.value) return;
    showDeleteConfirm.value = false;
    pageToDelete.value = null;
};

const performDelete = async () => {
    if (!pageToDelete.value || deleting.value) return;
    const pageId = pageToDelete.value.id;
    deleting.value = true;
    try {
        await store.deletePage(props.narrativeId, pageId);
        showDeleteConfirm.value = false;
        pageToDelete.value = null;
        emit('page-deleted', pageId);
    } catch {
        // Store surfaces the error.
    } finally {
        deleting.value = false;
    }
};

// ── Create ────────────────────────────────────────────────────────────────
const creating = ref(false);

const addPage = async (parentId: string | null = null) => {
    if (creating.value) return;
    closeMenu();
    creating.value = true;
    try {
        const page = await store.createPage(props.narrativeId, parentId);
        emit('select', page.id);
        // Drop straight into rename so the user can name the fresh page.
        await startRename(page);
    } catch {
        // Store surfaces the error.
    } finally {
        creating.value = false;
    }
};

const onSelect = (page: NarrativePage) => {
    if (editingPageId.value === page.id) return;
    emit('select', page.id);
};

const deleteBody = computed(() =>
    t('narratives.pages.deleteModal.message', { name: pageToDelete.value?.title ?? '' }),
);
</script>

<template>
    <div class="narrative-pages">
        <div class="narrative-pages-header">
            <h2 class="narrative-pages-title">{{ t('narratives.pages.heading') }}</h2>
        </div>

        <button
            type="button"
            class="narrative-pages-new"
            :disabled="creating"
            @click="addPage(null)"
        >
            <span class="material-icons">add</span>
            <span>{{ t('narratives.pages.newPage') }}</span>
        </button>

        <div class="narrative-pages-tree" @click="closeMenu">
            <div
                v-for="flat in flatPages"
                :key="flat.page.id"
                class="narrative-page-item"
                :class="{ 'narrative-page-item-active': flat.page.id === activePageId }"
                :style="{ paddingLeft: 12 + flat.depth * 16 + 'px' }"
                @click="onSelect(flat.page)"
                @dblclick="startRename(flat.page)"
            >
                <input
                    v-if="editingPageId === flat.page.id"
                    ref="renameInput"
                    v-model="editingTitle"
                    type="text"
                    class="narrative-page-rename"
                    @click.stop
                    @keyup.enter="commitRename"
                    @keyup.esc="cancelRename"
                    @blur="commitRename"
                />
                <span v-else class="narrative-page-name">{{ flat.page.title }}</span>

                <button
                    v-if="editingPageId !== flat.page.id"
                    type="button"
                    class="narrative-page-menu-button"
                    :title="t('narratives.pages.menu.aria')"
                    :aria-label="t('narratives.pages.menu.aria')"
                    @click="openMenu($event, flat.page.id)"
                >
                    ⋮
                </button>
            </div>
        </div>

        <!-- Kebab context menu -->
        <div
            v-if="menuPageId"
            class="context-menu"
            :style="{ top: menuPosition.y + 'px', left: menuPosition.x + 'px' }"
            @click.stop
        >
            <div
                class="context-menu-item"
                @click="pageById(menuPageId) && startRename(pageById(menuPageId)!)"
            >
                {{ t('narratives.pages.menu.rename') }}
            </div>
            <div
                v-if="depthOf(menuPageId) < MAX_DEPTH"
                class="context-menu-item"
                @click="addPage(menuPageId)"
            >
                {{ t('narratives.pages.menu.addSubPage') }}
            </div>
            <div
                class="context-menu-item context-menu-item-danger"
                :class="{ 'context-menu-item-disabled': !canDelete }"
                @click="canDelete && pageById(menuPageId) && confirmDelete(pageById(menuPageId)!)"
            >
                {{ t('narratives.pages.menu.delete') }}
            </div>
        </div>
        <div v-if="menuPageId" class="context-menu-backdrop" @click="closeMenu"></div>

        <!-- Delete confirmation -->
        <div v-if="showDeleteConfirm" class="modal-overlay" @click="cancelDelete">
            <div class="modal-content" @click.stop>
                <div class="modal-title">{{ t('narratives.pages.deleteModal.title') }}</div>
                <p>{{ deleteBody }}</p>
                <div class="modal-buttons">
                    <button class="modal-button-cancel" :disabled="deleting" @click="cancelDelete">
                        {{ t('common.cancel') }}
                    </button>
                    <button class="modal-button-delete" :disabled="deleting" @click="performDelete">
                        {{ t('common.delete') }}
                    </button>
                </div>
                <div
                    v-if="deleting"
                    class="modal-progress-bar"
                    :aria-label="t('narratives.pages.deleteModal.progress')"
                ></div>
            </div>
        </div>
    </div>
</template>
